import styled, { css, keyframes } from "styled-components";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// interfaces
import { SavingWrapperProps } from "@payment-ui/components/tick-screen/interfaces";

// constants
import { PERCENTAGE_ICON_HEIGHT, PERCENTAGE_ICON_WIDTH } from "@payment-ui/components/tick-screen/constants";

const DEFAULT_DELAY = 1.5;
const MULTIPLIER = 0.5;

const shouldRotate = (index: number) => index / 2 !== 0;
const getAnimationDelay = (index: number, value: number) => DEFAULT_DELAY + (index + value) * MULTIPLIER;

export const Container = styled.div`
    position: absolute;
    top: 3rem;
    @supports (top: max(3rem, calc(var(--sat, 0px) + 3rem))) {
        top: max(3rem, calc(var(--sat, 0px) + 3rem));
    }
    z-index: 1;
    width: 100%;
    display: flex;
    text-align: center;
    @supports (-webkit-touch-callout: none) {
        @supports (top: env(safe-area-inset-top)) {
            top: calc(2.5rem + env(safe-area-inset-top));
        }
    }
`;

export const Savings = styled(Text)(({ theme }) => ({
    marginLeft: "0.4rem",
    width: "auto",

    b: {
        ...theme.typography["V2.body1.neutral.bold"],
    },
}));

export const SavingsWrapper = styled.div<SavingWrapperProps>(({ theme, isFreeDel }) => ({
    display: "flex",
    alignItems: "center",
    borderRadius: theme.cornerRadius.small,
    background: isFreeDel ? theme.colors[theme.colorMode].rain_Sub : "rgba(221, 251, 239, 0.70)",
    borderColor: isFreeDel ? "rgba(101, 65, 228, 0.30)" : "rgba(27, 166, 114, 0.40)",
    borderStyle: "solid",
    borderWidth: "1px",
    padding: "0.625rem",
    margin: "0 1rem",
    overflow: "hidden",
    width: "100%",
}));

export const IconWrapper = styled.div`
    height: ${PERCENTAGE_ICON_WIDTH}px;
    width: ${PERCENTAGE_ICON_HEIGHT}px;
`;

export const FlexGrow = styled.div`
    flex-grow: 1;
`;

// Black membership text
export const BlackBenefitsContainer = styled.div<{ isOnlyOneLine: boolean; isBolt?: boolean }>(
    ({ isOnlyOneLine, theme, isBolt }) => ({
        display: "table",
        padding: theme.spacing.small,
        minWidth: "20%",
        margin: `calc(${isOnlyOneLine ? theme.spacing.large : theme.spacing.small} * -1) auto`,
        ...(isBolt && {
            marginTop: "5px",
        }),
    }),
);

const getTransitionValue = (value: number, index?: number) => {
    if (index !== undefined && shouldRotate(index)) {
        return `translateY(${value}px) rotate(180deg)`;
    }
    return `translateY(${value}px)`;
};

const swipeUpAnimation = (index?: number, opacity = 1) => keyframes`
    0% {
        opacity: 0;
        transform: ${getTransitionValue(6, index)};
    }
    100% {
        opacity: ${opacity};
        transform: ${getTransitionValue(0, index)};
    }
`;

export const BlackBenefitText = styled(Text)<{ index: number }>`
    text-align: center;
    letter-spacing: 1.5px;
    opacity: 0;
    text-transform: uppercase;
    animation: ${({ index }) => css`
        ${swipeUpAnimation()} 0.5s cubic-bezier(0.46, 0.03, 0.52, 0.96) ${getAnimationDelay(index, 1)}s forwards
    `};
`;

export const FirstBorderBottom = styled.div<{ index: number }>`
    width: 100%;
    height: 1px;
    opacity: 0;
    background: linear-gradient(
        91deg,
        rgba(179, 151, 131, 0) 0.33%,
        rgba(110, 80, 83, 0.6) 50.45%,
        rgba(255, 231, 183, 0) 100.57%
    );
    filter: blur(0px);
    animation: ${({ index }) => css`
        ${swipeUpAnimation(index, 0.4)} 0.2s cubic-bezier(0.46, 0.03, 0.52, 0.96) ${getAnimationDelay(
            index,
            1.5,
        )}s forwards
    `};
`;

export const BorderBottom = styled.div<{ index: number }>`
    opacity: 0;
    max-width: 65%;
    max-height: 20px;
    margin: calc(${({ theme }) => theme.spacing.xxSmall} * -1) auto 0;
    margin-bottom: calc(${({ theme }) => theme.spacing.xxSmall} * -1);
    animation: ${({ index }) => css`
        ${swipeUpAnimation(index)} 0.2s cubic-bezier(0.46, 0.03, 0.52, 0.96) ${getAnimationDelay(index, 1.5)}s forwards
    `};
`;
